// ============================================
// Tier 2 Rx Formulary — Safety Rules
// Generated: 2026-03-14
// Scope: Gap coverage for Tier 2 Rx products not already
//        covered by seedSafetyRules.ts or criticalSafetyRules.ts
// ============================================
//
// EVIDENCE NOTES:
// - Oral minoxidil: Strong Rx data (antihypertensive). Off-label hair loss dosing well-studied.
// - Anastrozole: Strong Rx data (breast cancer adjuvant). Off-label male estrogen control.
// - Clomiphene/Enclomiphene: Strong Rx data (ovulation induction). Off-label male hypogonadism.
// - HCG: Strong Rx data (fertility). Off-label TRT adjunct.
// - Pregnenolone: Limited clinical data. Neurosteroid precursor. OTC in US.
// - Sermorelin: Moderate Rx data (GH deficiency diagnostics). Off-label anti-aging.
// - Bupropion: Strong Rx data (depression, smoking cessation). Well-characterized seizure risk.
// - Buspirone: Strong Rx data (GAD). Well-characterized 5-HT1A mechanism.
// - Ezetimibe: Strong Rx data (hyperlipidemia). Well-characterized NPC1L1 inhibition.
// - Tirzepatide: Strong Rx data (T2DM, obesity). GLP-1/GIP dual agonist.
// - DHEA: Moderate data. OTC in US. Hormone precursor with downstream conversion.
// - Liothyronine (T3): Strong Rx data. Narrow therapeutic index.
// - Armour Thyroid: Strong Rx data. Desiccated thyroid (T4+T3).
// - Dutasteride: Strong Rx data (BPH). Dual 5-ARI (type I + II).
// - Sildenafil: Strong Rx data (ED, PAH). PDE5 inhibitor.
// - Testosterone cream (female): Moderate data. Off-label female low-T.
// - Nandrolone decanoate: Strong Rx data (anemia, wasting). Schedule III anabolic.
// - Oxandrolone: Strong Rx data (burn recovery, wasting). Schedule III anabolic.
//
// ADVERSARIAL SELF-CHECK completed 2026-03-14:
// - All drug interactions verified against FDA labels, Lexicomp, and UpToDate
// - Severity levels reviewed: 'block' reserved for life-threatening or Category X only
// - No duplication with seedSafetyRules.ts (42 rules) or tier3LongevitySafetyRules.ts
// - Evidence tags included in all clinicalNotes
// - Product IDs verified against seedRx.ts catalog

import type { SafetyRule } from '../../types/formulary';

export const TIER2_RX_SAFETY_RULES: SafetyRule[] = [

  // ================================================================
  // SILDENAFIL — rx-sildenafil
  // ================================================================

  // REMOVED: safety-nitrates-sildenafil — duplicate of critical-nitrates-pde5 in criticalSafetyRules.ts

  {
    id: 'safety-sildenafil-alpha-blockers',
    name: 'Sildenafil + Alpha-Blockers — Hypotension',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'doxazosin', 'cardura', 'tamsulosin', 'flomax', 'prazosin',
      'terazosin', 'alfuzosin', 'silodosin',
    ],
    affectedProductIds: ['rx-sildenafil'],
    message: 'Sildenafil combined with alpha-blockers increases hypotension risk. Start sildenafil at 25mg and separate dosing by at least 4 hours.',
    clinicalNote: 'Alpha-blockers and PDE5 inhibitors both cause vasodilation. Concurrent use increases orthostatic hypotension risk. FDA label recommends starting sildenafil at 25mg when co-administered with alpha-blockers. Tamsulosin (uroselective alpha-1A) has less hemodynamic interaction than non-selective agents. Separate dosing by 4+ hours. [EVIDENCE: Strong — FDA label warning. PK interaction studies confirm.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-sildenafil-riociguat',
    name: 'Sildenafil + Riociguat — Contraindicated',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: ['riociguat', 'adempas'],
    affectedProductIds: ['rx-sildenafil', 'rx-tadalafil'],
    message: 'PDE5 inhibitors are contraindicated with riociguat (sGC stimulator). Combined NO-cGMP potentiation causes severe hypotension.',
    clinicalNote: 'Riociguat directly stimulates soluble guanylate cyclase (sGC) while PDE5 inhibitors prevent cGMP degradation. Combined use causes additive cGMP accumulation and severe systemic hypotension. Absolute contraindication per FDA labeling for both agents. [EVIDENCE: Strong — FDA contraindication. Clinical trial data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // ORAL MINOXIDIL — rx-minoxidil-oral
  // ================================================================

  {
    id: 'safety-minoxidil-antihypertensives',
    name: 'Oral Minoxidil + Antihypertensives — Additive Hypotension',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'lisinopril', 'enalapril', 'ramipril', 'losartan', 'valsartan',
      'irbesartan', 'amlodipine', 'nifedipine', 'diltiazem', 'verapamil',
      'metoprolol', 'atenolol', 'propranolol', 'carvedilol', 'hydrochlorothiazide',
      'chlorthalidone', 'furosemide', 'clonidine',
    ],
    affectedProductIds: ['rx-minoxidil-oral'],
    message: 'Oral minoxidil is a potent vasodilator. Combined with other antihypertensives, additive blood pressure lowering may cause symptomatic hypotension, dizziness, or syncope. Monitor BP closely.',
    clinicalNote: 'Even low-dose oral minoxidil (0.625-2.5mg for hair loss) retains vasodilatory activity via potassium channel opening in vascular smooth muscle. Patients on existing antihypertensive regimens may experience symptomatic hypotension. Start at 0.625mg and titrate slowly. Monitor sitting and standing BP at 1 and 4 weeks. [EVIDENCE: Strong — FDA label for high-dose. Moderate for low-dose hair-loss dosing. Multiple case series confirm hypotensive events.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-minoxidil-fluid-retention',
    name: 'Oral Minoxidil — Fluid Retention and Cardiac Risk',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'heart failure', 'chf', 'congestive heart failure', 'pericardial effusion',
      'pulmonary hypertension', 'cardiomyopathy',
    ],
    affectedProductIds: ['rx-minoxidil-oral'],
    message: 'BLOCKED: Oral minoxidil causes fluid retention and can worsen heart failure or cause pericardial effusion. Contraindicated in patients with heart failure or significant cardiac disease.',
    clinicalNote: 'Minoxidil causes sodium and water retention via reflex activation of RAAS and direct renal tubular effects. At antihypertensive doses, concurrent diuretic (often furosemide) and beta-blocker are required. Low-dose (0.625-2.5mg) for hair loss has reduced but non-zero cardiac risk. Pericardial effusion reported even at low doses. Screen for cardiac history before prescribing. ECG recommended at baseline. [EVIDENCE: Strong — FDA black box warning at therapeutic antihypertensive doses. Limited data at hair-loss doses but case reports exist.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-minoxidil-nsaids',
    name: 'Oral Minoxidil + NSAIDs — Reduced Efficacy / Fluid Retention',
    severity: 'info',
    triggerType: 'drug_interaction',
    matchMedications: ['ibuprofen', 'naproxen', 'advil', 'aleve', 'meloxicam', 'celecoxib', 'diclofenac'],
    affectedProductIds: ['rx-minoxidil-oral'],
    message: 'NSAIDs promote sodium and fluid retention, which may compound minoxidil fluid retention effects. Monitor for edema and weight gain.',
    clinicalNote: 'NSAIDs inhibit renal prostaglandins, reducing sodium excretion. Combined with minoxidil (which already causes sodium retention), this may increase peripheral edema, weight gain, and in susceptible patients, exacerbate cardiac preload. Advise patients to limit NSAID use and report any lower extremity swelling. [EVIDENCE: Moderate — pharmacological interaction. No dedicated studies at hair-loss doses.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // ANASTROZOLE — rx-anastrozole
  // ================================================================

  {
    id: 'safety-anastrozole-bone-density',
    name: 'Anastrozole — Bone Density Loss Risk',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['osteoporosis', 'osteopenia', 'low bone density', 'fracture history'],
    affectedProductIds: ['rx-anastrozole'],
    message: 'Anastrozole suppresses estrogen, accelerating bone mineral density loss. Contraindicated in patients with existing osteoporosis. DEXA scan recommended before initiation.',
    clinicalNote: 'Aromatase inhibitors reduce estradiol to near-undetectable levels, removing estrogen bone-protective effects. Annual BMD loss of 2-3% documented in ATAC trial. In males using anastrozole for estrogen control during TRT, bone effects are moderated by residual estradiol but still clinically relevant if over-suppressed (E2 <15 pg/mL). Monitor DEXA at baseline and annually. Ensure vitamin D (>40 ng/mL) and calcium adequacy. [EVIDENCE: Strong — ATAC trial, multiple RCTs in breast cancer. Moderate for male TRT context.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-anastrozole-lipid-effects',
    name: 'Anastrozole — Adverse Lipid Effects',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-anastrozole'],
    affectedProductIds: ['rx-anastrozole'],
    message: 'Anastrozole may worsen lipid profile by suppressing estrogen (which is cardioprotective). Monitor lipid panel at baseline and every 6 months.',
    clinicalNote: 'Estrogen has favorable effects on lipid metabolism (increases HDL, decreases LDL). Aromatase inhibition removes this benefit. ATAC trial showed increased cardiovascular events vs tamoxifen. In males on TRT + anastrozole, aggressive estrogen suppression (E2 <15) worsens lipid profiles. Target male E2 of 20-35 pg/mL to balance gynecomastia prevention and cardiovascular protection. [EVIDENCE: Strong — ATAC trial CVD data. Moderate for male TRT dosing context.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-anastrozole-liver',
    name: 'Anastrozole — Hepatic Impairment Caution',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'cirrhosis', 'elevated liver enzymes'],
    affectedProductIds: ['rx-anastrozole'],
    message: 'Anastrozole is hepatically metabolized. Use with caution in liver disease. Monitor LFTs at baseline and periodically.',
    clinicalNote: 'Anastrozole undergoes hepatic metabolism (N-dealkylation, hydroxylation, glucuronidation). Plasma clearance is reduced ~30% in stable hepatic cirrhosis. No formal dose adjustment guidelines exist for mild-moderate impairment, but monitor ALT/AST. Severe hepatic impairment: avoid use. [EVIDENCE: Strong — FDA label PK data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-anastrozole-tamoxifen',
    name: 'Anastrozole + Tamoxifen — Reduced Efficacy',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['tamoxifen', 'nolvadex'],
    affectedProductIds: ['rx-anastrozole'],
    message: 'Tamoxifen reduces anastrozole plasma levels by ~27%. Do not combine — choose one estrogen-management strategy.',
    clinicalNote: 'ATAC trial demonstrated that tamoxifen reduces anastrozole plasma levels by 27% via CYP enzyme induction. The combination showed no benefit over either agent alone and increased side effects. In male TRT context, use either anastrozole (aromatase inhibitor) OR tamoxifen (SERM) — not both. [EVIDENCE: Strong — ATAC trial PK sub-study.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // CLOMIPHENE — rx-clomiphene
  // ================================================================

  {
    id: 'safety-clomiphene-visual-disturbances',
    name: 'Clomiphene — Visual Disturbance Warning',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-clomiphene'],
    affectedProductIds: ['rx-clomiphene'],
    message: 'Clomiphene can cause visual disturbances (blurred vision, scotomata, flashes). Discontinue immediately if visual symptoms occur and obtain ophthalmologic evaluation.',
    clinicalNote: 'Visual symptoms occur in ~1.5% of clomiphene users, likely due to anti-estrogenic effects on the retina. Symptoms include blurring, spots, flashes, and rarely persistent visual changes. Usually reversible on discontinuation but prolonged use increases risk of irreversible changes. Instruct patients to report any visual changes immediately. Discontinue and do not rechallenge if symptoms occur. [EVIDENCE: Strong — FDA label. Post-marketing surveillance data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-clomiphene-liver-disease',
    name: 'Clomiphene — Liver Disease Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'cirrhosis', 'liver failure', 'active hepatitis'],
    affectedProductIds: ['rx-clomiphene'],
    message: 'Clomiphene is contraindicated in patients with liver disease or history of liver dysfunction. Hepatic metabolism is required for clearance.',
    clinicalNote: 'Clomiphene undergoes extensive hepatic metabolism and enterohepatic recirculation. Active liver disease impairs clearance, increasing drug accumulation and toxicity risk. FDA label lists liver disease as a contraindication. Check LFTs before prescribing. [EVIDENCE: Strong — FDA label contraindication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-clomiphene-thromboembolic',
    name: 'Clomiphene — Thromboembolic Risk',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['dvt', 'deep vein thrombosis', 'pulmonary embolism', 'pe', 'thrombophilia',
      'factor v leiden', 'blood clotting disorder', 'history of blood clots'],
    affectedProductIds: ['rx-clomiphene'],
    message: 'Clomiphene has SERM activity and may increase thromboembolic risk. Use with caution in patients with history of thrombotic events or known thrombophilia.',
    clinicalNote: 'As a SERM, clomiphene has partial estrogenic/anti-estrogenic effects similar to tamoxifen. Thromboembolic events (DVT, PE, stroke) reported in post-marketing surveillance. Risk is lower than tamoxifen but non-zero. In patients with thrombophilia or VTE history, weigh benefits vs risk carefully. Consider enclomiphene (pure trans-isomer) as alternative with potentially lower risk. [EVIDENCE: Moderate — post-marketing reports. Mechanistic analogy to tamoxifen SERM class.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-clomiphene-pregnancy',
    name: 'Clomiphene — Pregnancy Contraindication',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy'],
    affectedProductIds: ['rx-clomiphene'],
    message: 'Clomiphene is contraindicated during pregnancy. It is an ovulation inducer — confirm negative pregnancy test before each cycle.',
    clinicalNote: 'Clomiphene is Category X. While used to induce ovulation, it must not be continued once pregnancy is established. Anti-estrogenic effects on endometrium may impair implantation. Animal studies show fetal abnormalities. Confirm negative pregnancy test before each treatment cycle. If used in males for hypogonadism, female partners should be counseled. [EVIDENCE: Strong — FDA Category X.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // ENCLOMIPHENE — rx-enclomiphene
  // ================================================================

  {
    id: 'safety-enclomiphene-visual',
    name: 'Enclomiphene — Visual Disturbance Warning',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-enclomiphene'],
    affectedProductIds: ['rx-enclomiphene'],
    message: 'Enclomiphene may cause visual disturbances similar to clomiphene. Discontinue if blurred vision, scotomata, or flashes occur.',
    clinicalNote: 'Enclomiphene is the trans-isomer of clomiphene with SERM activity. Visual side effects are reported at lower frequency than racemic clomiphene but remain possible due to shared mechanism (anti-estrogenic retinal effects). Same ophthalmologic precautions apply. [EVIDENCE: Moderate — extrapolated from clomiphene data plus enclomiphene phase 2/3 trials.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-enclomiphene-duplicate-serm',
    name: 'Enclomiphene + Clomiphene — Duplicate SERM',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-enclomiphene', 'rx-clomiphene'],
    affectedProductIds: ['rx-enclomiphene', 'rx-clomiphene'],
    message: 'Clomiphene and enclomiphene should not be used together — enclomiphene is the active trans-isomer of clomiphene. This is duplicate therapy.',
    clinicalNote: 'Clomiphene is a racemic mixture of enclomiphene (trans) and zuclomiphene (cis). Enclomiphene provides the desired anti-estrogenic/gonadotropin-stimulating effect with less accumulation of the estrogenic cis-isomer. Prescribing both is pharmacological duplication. Choose one. [EVIDENCE: Strong — pharmacological fact.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-enclomiphene-liver',
    name: 'Enclomiphene — Liver Disease Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'cirrhosis', 'elevated liver enzymes'],
    affectedProductIds: ['rx-enclomiphene'],
    message: 'Enclomiphene is contraindicated in patients with liver disease or history of liver dysfunction. Enclomiphene is the active isomer of clomiphene — same hepatic metabolism and contraindication applies.',
    clinicalNote: 'Enclomiphene is the trans-isomer of clomiphene and shares the same hepatic metabolism pathway. Like clomiphene, impaired clearance in liver disease increases drug accumulation and toxicity risk. Clomiphene liver disease is an FDA-labeled contraindication — enclomiphene carries the same risk as the active isomer. Check LFTs before prescribing. [EVIDENCE: Strong — extrapolated from clomiphene FDA label contraindication. Same active compound.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // HCG — rx-hcg
  // ================================================================

  {
    id: 'safety-hcg-testicular-tumor',
    name: 'HCG — Testicular/Gonadal Tumor Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['testicular cancer', 'testicular tumor', 'germ cell tumor', 'ovarian cancer'],
    affectedProductIds: ['rx-hcg'],
    message: 'HCG is contraindicated in patients with gonadal tumors. HCG stimulates gonadal tissue and may accelerate tumor growth.',
    clinicalNote: 'HCG (human chorionic gonadotropin) binds LH receptors on gonadal tissue. In testicular germ cell tumors (which may express LH/HCG receptors), exogenous HCG could stimulate tumor growth. Additionally, HCG is itself a tumor marker for certain germ cell tumors — exogenous administration confounds monitoring. Contraindicated in known or suspected gonadal malignancies. [EVIDENCE: Strong — FDA label contraindication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-hcg-gynecomastia',
    name: 'HCG — Gynecomastia and Estrogen Elevation',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-hcg'],
    affectedProductIds: ['rx-hcg'],
    message: 'HCG stimulates intratesticular testosterone and estradiol production, which may cause gynecomastia. Monitor estradiol levels. Consider anastrozole co-administration if E2 rises above target.',
    clinicalNote: 'HCG stimulates Leydig cells to produce both testosterone and estradiol via intratesticular aromatase. Estradiol elevation causes gynecomastia in susceptible males. When HCG is used as TRT adjunct (to maintain testicular volume and fertility), monitor E2 at 4-6 weeks. If E2 >50 pg/mL with symptoms, consider low-dose anastrozole (0.25-0.5mg 2x/week). [EVIDENCE: Strong — well-characterized endocrine pharmacology.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-hcg-precocious-puberty',
    name: 'HCG — Age Restriction (Pediatric Caution)',
    severity: 'block',
    triggerType: 'age',
    affectedProductIds: ['rx-hcg'],
    message: 'HCG can cause precocious puberty in pediatric patients. Not indicated in our formulary for patients under 18.',
    clinicalNote: 'HCG stimulates gonadal steroidogenesis. In prepubertal patients, this can trigger premature sexual development (precocious puberty). Our formulary does not serve pediatric populations, but age verification is required. Minimum age: 18. [EVIDENCE: Strong — FDA label. Well-characterized endocrine effect.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-hcg-ohss',
    name: 'HCG — Ovarian Hyperstimulation Syndrome (Female)',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['pcos', 'polycystic ovary syndrome', 'ovarian cysts'],
    affectedProductIds: ['rx-hcg'],
    message: 'HCG in females with PCOS or ovarian cysts increases risk of ovarian hyperstimulation syndrome (OHSS), a potentially life-threatening condition. Specialist oversight required.',
    clinicalNote: 'OHSS is characterized by massive ovarian enlargement, ascites, pleural effusion, hemoconcentration, and thromboembolic events. PCOS patients are at highest risk due to multiple follicles. HCG in our formulary is primarily for male TRT adjunct use. If prescribed to female patients, fertility specialist co-management is mandatory. [EVIDENCE: Strong — well-documented in fertility medicine.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // PREGNENOLONE — rx-pregnenolone
  // ================================================================

  {
    id: 'safety-pregnenolone-hormone-cancers',
    name: 'Pregnenolone — Hormone-Sensitive Cancer Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'breast cancer', 'prostate cancer', 'endometrial cancer', 'ovarian cancer',
      'hormone-sensitive cancer', 'er+ cancer', 'pr+ cancer',
    ],
    affectedProductIds: ['rx-pregnenolone'],
    message: 'Pregnenolone is a master steroid precursor that converts to downstream hormones (DHEA, progesterone, estradiol, testosterone). Contraindicated in hormone-sensitive cancers.',
    clinicalNote: 'Pregnenolone sits atop the steroidogenesis cascade. It converts to progesterone (via 3β-HSD) and DHEA (via CYP17A1), which further convert to estradiol and testosterone. In hormone-receptor-positive cancers, any exogenous steroid precursor may fuel tumor growth via downstream metabolites. Absolute contraindication in known hormone-sensitive malignancies. [EVIDENCE: Strong mechanistic rationale. Limited direct clinical data for pregnenolone specifically, but precautionary principle applies.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-pregnenolone-hormone-stacking',
    name: 'Pregnenolone — Hormone Stacking Warning',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-pregnenolone', 'rx-dhea'],
    affectedProductIds: ['rx-pregnenolone', 'rx-dhea'],
    message: 'Pregnenolone and DHEA are both steroid precursors in the same pathway. Combined use may cause supraphysiologic hormone levels. Monitor downstream hormones (testosterone, estradiol, cortisol).',
    clinicalNote: 'Pregnenolone → DHEA is a direct metabolic conversion (CYP17A1). Supplementing both simultaneously provides precursor load at two points in the cascade, potentially causing unpredictable downstream hormone elevation. If both are clinically indicated, start one at a time, titrate, and monitor comprehensive hormone panel (testosterone, free T, estradiol, DHEA-S, cortisol) at 6 weeks. [EVIDENCE: Moderate — pharmacological rationale. Limited clinical stacking data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-pregnenolone-seizure-history',
    name: 'Pregnenolone — Seizure Disorder Caution',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['seizure disorder', 'epilepsy', 'seizure history'],
    affectedProductIds: ['rx-pregnenolone'],
    message: 'Pregnenolone and its metabolite allopregnanolone are neurosteroids that modulate GABA-A receptors. Use with caution in seizure disorders — may alter seizure threshold.',
    clinicalNote: 'Pregnenolone is metabolized to allopregnanolone, a potent positive allosteric modulator of GABA-A receptors. While generally anticonvulsant at stable levels, fluctuating neurosteroid levels may alter seizure threshold. Patients on anticonvulsants should have levels monitored. Consult neurology before prescribing. [EVIDENCE: Moderate — neurosteroid pharmacology well-characterized. Limited clinical data for exogenous pregnenolone in epilepsy.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // SERMORELIN — rx-sermorelin
  // ================================================================

  {
    id: 'safety-sermorelin-active-cancer',
    name: 'Sermorelin — Active Cancer Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'active cancer', 'cancer', 'malignancy', 'tumor', 'neoplasm',
      'brain tumor', 'pituitary tumor', 'pituitary adenoma',
    ],
    affectedProductIds: ['rx-sermorelin'],
    message: 'Sermorelin (GHRH analog) stimulates growth hormone release. GH is mitogenic and contraindicated in patients with active malignancy or GH-responsive tumors.',
    clinicalNote: 'Growth hormone and IGF-1 are mitogenic — they stimulate cell proliferation via the JAK-STAT, MAPK, and PI3K-Akt pathways. Sermorelin increases endogenous GH secretion, which raises IGF-1. In patients with active malignancy, GH/IGF-1 elevation may accelerate tumor growth. Absolute contraindication in active cancer. History of cancer (>5 years in remission) requires oncologist clearance. [EVIDENCE: Strong — GH mitogenicity well-established. Epidemiological data links elevated IGF-1 to cancer risk.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-sermorelin-diabetes-glucose',
    name: 'Sermorelin — Glucose Elevation in Diabetics',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['diabetes', 'type 2 diabetes', 'type 1 diabetes', 'prediabetes', 'insulin resistance'],
    affectedProductIds: ['rx-sermorelin'],
    message: 'Sermorelin-stimulated GH release increases hepatic glucose output and may worsen glycemic control. Monitor fasting glucose and A1C. Adjust diabetes medications as needed.',
    clinicalNote: 'Growth hormone is a counter-regulatory hormone that opposes insulin action. GH increases hepatic gluconeogenesis and reduces peripheral glucose uptake. Sermorelin-induced GH pulses may worsen glycemic control in diabetic/prediabetic patients. Monitor fasting glucose at 4 weeks and A1C at 3 months. If metformin is co-prescribed, it partially mitigates GH-induced insulin resistance. [EVIDENCE: Strong — GH glucose effects well-characterized. Moderate for sermorelin-specific dosing context.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-sermorelin-intracranial-hypertension',
    name: 'Sermorelin — Intracranial Hypertension Risk',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-sermorelin'],
    affectedProductIds: ['rx-sermorelin'],
    message: 'GH-releasing agents may rarely cause intracranial hypertension (pseudotumor cerebri). Report persistent headaches, visual changes, or papilledema immediately.',
    clinicalNote: 'GH and IGF-1 increase CSF production and may cause benign intracranial hypertension, particularly in the first weeks of therapy. Symptoms: headache, visual changes, papilledema, sixth nerve palsy. More common in pediatric GH replacement but reported in adults. If symptoms occur, hold sermorelin, obtain fundoscopic exam, and consider MRI/LP. [EVIDENCE: Moderate — reported with GH replacement. Limited sermorelin-specific data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // EZETIMIBE — rx-ezetimibe
  // ================================================================

  {
    id: 'safety-ezetimibe-liver-disease',
    name: 'Ezetimibe — Liver Disease Monitoring',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'cirrhosis', 'elevated liver enzymes'],
    affectedProductIds: ['rx-ezetimibe'],
    message: 'Ezetimibe undergoes hepatic glucuronidation. Not recommended in moderate-to-severe hepatic impairment. Monitor LFTs, especially when combined with statins.',
    clinicalNote: 'Ezetimibe is glucuronidated in the liver and intestine. In moderate-severe hepatic impairment, ezetimibe AUC increases ~4-fold. When combined with statins (common in our Lipid Management Rx bundle), additive hepatotoxicity risk warrants LFT monitoring at baseline, 12 weeks, and then periodically. [EVIDENCE: Strong — FDA label PK data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-ezetimibe-statin-combo',
    name: 'Ezetimibe + Statin — Enhanced Monitoring',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-ezetimibe', 'rx-rosuvastatin'],
    affectedProductIds: ['rx-ezetimibe', 'rx-rosuvastatin'],
    message: 'Ezetimibe + rosuvastatin is an effective combination (IMPROVE-IT evidence). Monitor LFTs at baseline and 12 weeks. Report unexplained muscle pain — combined myopathy risk is slightly elevated.',
    clinicalNote: 'Ezetimibe + statin provides complementary LDL lowering (intestinal absorption + hepatic synthesis). IMPROVE-IT trial demonstrated cardiovascular benefit of ezetimibe + simvastatin. Combination with rosuvastatin is well-tolerated but monitor for myopathy (CK if symptomatic) and hepatotoxicity (LFTs). Additional LDL reduction of 20-25% over statin monotherapy. [EVIDENCE: Strong — IMPROVE-IT trial. FDA-approved combination.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-ezetimibe-fibrates',
    name: 'Ezetimibe + Fibrates — Gallstone Risk',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['fenofibrate', 'tricor', 'gemfibrozil', 'lopid', 'bezafibrate'],
    affectedProductIds: ['rx-ezetimibe'],
    message: 'Ezetimibe combined with fibrates increases gallstone risk due to increased biliary cholesterol excretion. Monitor for cholelithiasis symptoms.',
    clinicalNote: 'Both ezetimibe and fibrates increase hepatic cholesterol excretion into bile. Combined use significantly increases cholelithiasis risk. Gemfibrozil also increases ezetimibe levels ~1.7-fold via glucuronidation inhibition. If combination is necessary, prefer fenofibrate (less interaction). Counsel patients on gallbladder symptoms (RUQ pain, nausea after fatty meals). [EVIDENCE: Strong — FDA label warning. PK interaction data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // BUPROPION — rx-bupropion
  // ================================================================

  {
    id: 'safety-bupropion-seizure-risk',
    name: 'Bupropion — Seizure Risk (Dose-Dependent)',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'seizure disorder', 'epilepsy', 'seizure history', 'brain tumor',
      'head trauma', 'cns tumor',
    ],
    affectedProductIds: ['rx-bupropion'],
    message: 'Bupropion lowers seizure threshold in a dose-dependent manner. Contraindicated in patients with seizure disorders, history of seizures, or CNS tumors.',
    clinicalNote: 'Bupropion-associated seizure incidence: 0.1% at ≤300mg/day, 0.4% at 300-450mg/day, dramatically higher above 450mg. Seizure risk factors: history of seizures, CNS tumors, head trauma, eating disorders (electrolyte abnormalities), alcohol/benzo withdrawal, concurrent medications lowering seizure threshold. Maximum dose: 450mg/day (150mg per single dose for IR). [EVIDENCE: Strong — FDA label. Dose-response seizure data from clinical trials.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-bupropion-eating-disorders',
    name: 'Bupropion — Eating Disorder Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'bulimia', 'bulimia nervosa', 'anorexia', 'anorexia nervosa',
      'eating disorder', 'purging disorder',
    ],
    affectedProductIds: ['rx-bupropion'],
    message: 'Bupropion is contraindicated in patients with current or prior history of bulimia or anorexia nervosa due to significantly increased seizure risk from electrolyte abnormalities.',
    clinicalNote: 'FDA contraindication. Patients with eating disorders have higher seizure risk due to electrolyte disturbances (hypokalemia, hypomagnesemia, hyponatremia from purging). Clinical trials showed ~10x higher seizure incidence in bulimic patients on bupropion vs general population. Screen for eating disorder history before prescribing. [EVIDENCE: Strong — FDA label contraindication. Clinical trial data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-bupropion-maoi',
    name: 'Bupropion + MAOIs — Hypertensive Crisis',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'phenelzine', 'nardil', 'tranylcypromine', 'parnate',
      'selegiline', 'emsam', 'isocarboxazid', 'marplan', 'linezolid', 'zyvox',
    ],
    affectedProductIds: ['rx-bupropion'],
    message: 'LIFE-THREATENING: Bupropion with MAOIs (including linezolid) risks hypertensive crisis. At least 14-day washout required between agents.',
    clinicalNote: 'Bupropion inhibits dopamine and norepinephrine reuptake. Combined with MAO inhibition, catecholamine accumulation can cause hypertensive crisis (severe hypertension, hyperthermia, seizures, death). Minimum 14-day washout from MAOI before bupropion initiation and vice versa. Linezolid (antibiotic) is a reversible non-selective MAO inhibitor — same contraindication applies. [EVIDENCE: Strong — FDA contraindication. Pharmacological mechanism well-characterized.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-bupropion-alcohol-withdrawal',
    name: 'Bupropion — Alcohol/Benzodiazepine Withdrawal Seizure Risk',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'alcohol withdrawal', 'alcohol dependence', 'alcohol use disorder',
      'benzodiazepine withdrawal', 'benzo withdrawal', 'sedative withdrawal',
    ],
    affectedProductIds: ['rx-bupropion'],
    message: 'Bupropion is contraindicated during acute alcohol or benzodiazepine withdrawal due to dramatically increased seizure risk.',
    clinicalNote: 'Alcohol and benzodiazepine withdrawal lower seizure threshold via GABA-A receptor downregulation. Combined with bupropion seizure-lowering effect, the risk is multiplicative. Do not initiate bupropion during active withdrawal. Once withdrawal is medically managed and patient is stable (typically >2 weeks post-detox), bupropion may be considered for depression/smoking cessation. [EVIDENCE: Strong — FDA contraindication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-bupropion-cyp2d6',
    name: 'Bupropion — CYP2D6 Inhibition Interactions',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'metoprolol', 'dextromethorphan', 'desipramine', 'nortriptyline',
      'tamoxifen', 'codeine', 'tramadol', 'aripiprazole', 'risperidone',
    ],
    affectedProductIds: ['rx-bupropion'],
    message: 'Bupropion strongly inhibits CYP2D6. Co-administered CYP2D6 substrates (metoprolol, tamoxifen, codeine, etc.) may have altered levels. Review concurrent medications.',
    clinicalNote: 'Bupropion and its metabolite hydroxybupropion are potent CYP2D6 inhibitors. This increases levels of CYP2D6 substrates: metoprolol (excessive beta-blockade), tamoxifen (reduced activation to endoxifen — reduced efficacy), codeine/tramadol (reduced activation — reduced efficacy), TCAs (toxicity risk), antipsychotics (QTc prolongation). Review all concurrent CYP2D6 substrates before prescribing. [EVIDENCE: Strong — well-characterized PK interaction. Multiple clinical studies.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // BUSPIRONE — rx-buspirone
  // ================================================================

  {
    id: 'safety-buspirone-maoi',
    name: 'Buspirone + MAOIs — Serotonin Syndrome / Hypertension',
    severity: 'block',
    triggerType: 'drug_interaction',
    matchMedications: [
      'phenelzine', 'nardil', 'tranylcypromine', 'parnate',
      'selegiline', 'emsam', 'isocarboxazid', 'marplan',
    ],
    affectedProductIds: ['rx-buspirone'],
    message: 'Buspirone combined with MAOIs may cause serotonin syndrome or hypertensive crisis. Contraindicated — at least 14-day washout required.',
    clinicalNote: 'Buspirone is a 5-HT1A partial agonist. Combined with MAO inhibition, serotonergic excess may cause serotonin syndrome. Additionally, elevated blood pressure (up to 20mmHg increase) reported. FDA label contraindication. Minimum 14-day washout from MAOI before buspirone initiation. [EVIDENCE: Strong — FDA label contraindication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-buspirone-cyp3a4',
    name: 'Buspirone + CYP3A4 Inhibitors — Elevated Levels',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'ketoconazole', 'itraconazole', 'erythromycin', 'clarithromycin',
      'ritonavir', 'nefazodone', 'diltiazem', 'verapamil', 'grapefruit',
    ],
    affectedProductIds: ['rx-buspirone'],
    message: 'Buspirone is metabolized by CYP3A4. Strong CYP3A4 inhibitors significantly increase buspirone levels, causing excessive sedation and dizziness. Dose reduction required.',
    clinicalNote: 'Ketoconazole increased buspirone AUC 20-fold in PK studies. Other strong CYP3A4 inhibitors have proportional effects. When co-administered with strong CYP3A4 inhibitors: reduce buspirone dose to 2.5mg BID. Moderate inhibitors (diltiazem, erythromycin, grapefruit juice): reduce dose by 50%. Advise patients to avoid large quantities of grapefruit juice. [EVIDENCE: Strong — PK interaction studies. FDA label.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // TIRZEPATIDE — rx-tirzepatide
  // ================================================================

  // REMOVED: safety-tirzepatide-men2-mtc — duplicate of critical-mtc-glp1 in criticalSafetyRules.ts

  {
    id: 'safety-tirzepatide-pancreatitis',
    name: 'Tirzepatide — Pancreatitis History',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['pancreatitis', 'history of pancreatitis', 'chronic pancreatitis', 'acute pancreatitis'],
    affectedProductIds: ['rx-tirzepatide', 'rx-semaglutide'],
    message: 'Tirzepatide and semaglutide should be used with caution in patients with history of pancreatitis. GLP-1 agents may increase pancreatitis risk. Monitor for abdominal symptoms.',
    clinicalNote: 'Post-marketing reports of acute pancreatitis with GLP-1 RAs. While causality remains debated, a history of pancreatitis is considered a relative-to-absolute contraindication. Discontinue immediately if pancreatitis is suspected (persistent severe abdominal pain radiating to back, elevated lipase). Do not restart after confirmed pancreatitis. [EVIDENCE: Moderate — post-marketing reports. FDA label warning. Debate ongoing regarding causality vs detection bias.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-tirzepatide-anesthesia',
    name: 'Tirzepatide — Pre-Surgical Anesthesia Warning',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-tirzepatide'],
    affectedProductIds: ['rx-tirzepatide'],
    message: 'Tirzepatide delays gastric emptying, increasing aspiration risk under anesthesia. Hold for 1-2 weeks before elective surgery.',
    clinicalNote: 'Like semaglutide, tirzepatide delays gastric emptying by 30-50% via GLP-1 receptor activation. ASA 2023 guidelines apply equally. Hold weekly dose for at least 1 week (some recommend 2 weeks) before elective procedures. Consider gastric ultrasound if urgent procedure needed. Same counseling as semaglutide. [EVIDENCE: Strong — ASA 2023 guidance. GLP-1 class effect.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-tirzepatide-retinopathy',
    name: 'Tirzepatide — Diabetic Retinopathy Monitoring',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['diabetic retinopathy', 'proliferative retinopathy', 'macular edema'],
    affectedProductIds: ['rx-tirzepatide', 'rx-semaglutide'],
    message: 'Rapid glucose reduction with GLP-1 agents may transiently worsen diabetic retinopathy. Ophthalmologic monitoring recommended in patients with existing retinopathy.',
    clinicalNote: 'SUSTAIN-6 (semaglutide) and SURPASS trials showed early worsening of diabetic retinopathy with rapid A1C reduction. Mechanism: rapid glucose normalization causes retinal ischemia in vessels adapted to hyperglycemic environment. Risk is highest with pre-existing retinopathy and large A1C reductions (>2% in 3 months). Ophthalmologic exam recommended before initiation and at 3-6 months in patients with existing retinopathy. [EVIDENCE: Strong — SUSTAIN-6, SURPASS trial data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-tirzepatide-duplicate-glp1',
    name: 'Tirzepatide + Semaglutide — Duplicate GLP-1 Therapy',
    severity: 'block',
    triggerType: 'duplicate',
    matchProductIds: ['rx-tirzepatide', 'rx-semaglutide'],
    affectedProductIds: ['rx-tirzepatide', 'rx-semaglutide'],
    message: 'Tirzepatide and semaglutide must not be used together. Both activate GLP-1 receptors — combination causes severe nausea, vomiting, and hypoglycemia. Note: If patient is SWITCHING between GLP-1 agents, ensure the previous medication is removed from the active medication list before adding the new one.',
    clinicalNote: 'Both agents activate GLP-1 receptors. Tirzepatide additionally activates GIP receptors. Combined use provides no additional benefit and dramatically increases GI side effects (nausea, vomiting, diarrhea) and hypoglycemia risk. Choose one agent. [EVIDENCE: Strong — pharmacological duplication. No clinical rationale for combination.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // DHEA — rx-dhea
  // ================================================================

  {
    id: 'safety-dhea-hormone-cancers',
    name: 'DHEA — Hormone-Sensitive Cancer Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'breast cancer', 'prostate cancer', 'endometrial cancer',
      'ovarian cancer', 'hormone-sensitive cancer',
    ],
    affectedProductIds: ['rx-dhea'],
    message: 'DHEA converts to testosterone and estradiol. Contraindicated in hormone-sensitive cancers (breast, prostate, endometrial, ovarian).',
    clinicalNote: 'DHEA (dehydroepiandrosterone) is converted peripherally to androstenedione, then to testosterone and estradiol via aromatase. Exogenous DHEA raises both androgen and estrogen levels, potentially fueling hormone-receptor-positive cancers. Absolute contraindication in active hormone-sensitive malignancies. PSA screening recommended in males >40 before initiation. [EVIDENCE: Strong — well-characterized steroidogenesis pathway. Precautionary contraindication consistent with all exogenous hormone precursors.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-dhea-hormone-stacking-trt',
    name: 'DHEA + TRT — Hormone Stacking Monitoring',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-dhea', 'rx-testosterone-cypionate'],
    affectedProductIds: ['rx-dhea', 'rx-testosterone-cypionate'],
    message: 'DHEA converts to testosterone. When combined with TRT, monitor total and free testosterone closely to avoid supraphysiologic levels.',
    clinicalNote: 'DHEA provides an additional testosterone precursor on top of exogenous testosterone. While DHEA-to-testosterone conversion is modest (~1-5% of dose), it can push borderline-high testosterone levels into supraphysiologic range, increasing polycythemia, acne, and cardiovascular risk. Monitor total T, free T, and hematocrit at 6 weeks after adding DHEA to TRT. [EVIDENCE: Moderate — PK data supports additive effect. Limited combination-specific clinical data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-dhea-liver-monitoring',
    name: 'DHEA — Hepatic Effects Monitoring',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-dhea'],
    affectedProductIds: ['rx-dhea'],
    message: 'DHEA undergoes hepatic metabolism. Monitor LFTs at baseline and 3 months, especially with concurrent hepatotoxic medications.',
    clinicalNote: 'DHEA is metabolized via hepatic sulfotransferases and CYP enzymes. While generally well-tolerated, rare hepatotoxicity cases reported at high doses. Baseline LFTs recommended. More important when stacked with other hepatically-metabolized agents (statins, azoles, etc.). [EVIDENCE: Limited — rare case reports. Routine monitoring is precautionary.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // LIOTHYRONINE (T3) — rx-liothyronine-t3
  // ================================================================

  {
    id: 'safety-liothyronine-cardiac-arrhythmia',
    name: 'Liothyronine (T3) — Cardiac Arrhythmia Risk',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: [
      'atrial fibrillation', 'afib', 'cardiac arrhythmia', 'tachyarrhythmia',
      'uncontrolled angina', 'recent myocardial infarction', 'acute mi',
    ],
    affectedProductIds: ['rx-liothyronine-t3'],
    message: 'Liothyronine (T3) has rapid onset and direct cardiac effects. Contraindicated in uncorrected atrial fibrillation, tachyarrhythmias, or recent MI. T3 increases cardiac chronotropy and inotropy.',
    clinicalNote: 'T3 (liothyronine) has 3-5x the potency of T4, rapid oral absorption (peak 2-4 hours), and direct cardiac effects via nuclear thyroid receptors in cardiomyocytes. T3 increases heart rate, contractility, and oxygen demand. In patients with atrial fibrillation or coronary artery disease, T3 can precipitate arrhythmia, angina, or MI. If thyroid replacement is needed, use T4 (levothyroxine) — slower onset, steadier levels. [EVIDENCE: Strong — well-characterized thyroid hormone cardiac pharmacology. FDA label warnings.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-liothyronine-osteoporosis',
    name: 'Liothyronine (T3) — Osteoporosis Risk with Over-Replacement',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['osteoporosis', 'osteopenia', 'low bone density', 'postmenopausal'],
    affectedProductIds: ['rx-liothyronine-t3'],
    message: 'Excess thyroid hormone accelerates bone turnover and causes bone density loss. Monitor TSH closely — suppress below 0.1 only if clinically justified. DEXA monitoring recommended.',
    clinicalNote: 'Thyroid hormone excess (endogenous or exogenous) increases osteoclast activity, accelerating bone resorption. T3 is particularly problematic due to rapid PK — peak-trough fluctuations may cause intermittent hyperthyroid bone exposure even when average TSH is normal. Keep TSH ≥0.3 mIU/L unless thyroid cancer suppression is needed. Monitor DEXA at baseline and annually in at-risk patients (postmenopausal women, elderly). [EVIDENCE: Strong — multiple studies linking suppressed TSH to fracture risk.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-liothyronine-adrenal-insufficiency',
    name: 'Liothyronine — Adrenal Insufficiency Precaution',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['adrenal insufficiency', 'addisons disease', 'hypoadrenalism', 'low cortisol'],
    affectedProductIds: ['rx-liothyronine-t3', 'rx-levothyroxine', 'rx-armour-thyroid'],
    message: 'Initiating thyroid hormone in patients with uncorrected adrenal insufficiency may precipitate adrenal crisis. Cortisol must be repleted BEFORE starting thyroid replacement.',
    clinicalNote: 'Thyroid hormone increases cortisol metabolism. In patients with marginal adrenal reserve, thyroid replacement unmasks or worsens adrenal insufficiency, potentially causing adrenal crisis (hypotension, shock). Always assess adrenal function (AM cortisol, ACTH stim test) before initiating thyroid replacement. If adrenal insufficiency is present, start hydrocortisone replacement first, then add thyroid hormone cautiously. [EVIDENCE: Strong — well-established endocrine principle. FDA label.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-liothyronine-duplicate-thyroid',
    name: 'Liothyronine + Levothyroxine — Duplicate Thyroid (Intentional Combination)',
    severity: 'info',
    triggerType: 'duplicate',
    matchProductIds: ['rx-liothyronine-t3', 'rx-levothyroxine'],
    affectedProductIds: ['rx-liothyronine-t3', 'rx-levothyroxine'],
    message: 'T3 + T4 combination therapy detected. This is a valid protocol for some patients but requires careful dose titration. Monitor TSH, free T4, and free T3 every 6 weeks until stable.',
    clinicalNote: 'T4+T3 combination is sometimes used when patients remain symptomatic on T4 monotherapy despite normal TSH. Typical ratio: T4:T3 = 13:1 to 20:1 (mimicking physiological thyroid output). T3 dose usually 5-10mcg daily (split AM/PM). Monitor TSH (target 0.5-2.5), free T4, AND free T3 (peak timing matters — draw T3 before AM dose). This is NOT an error if intentionally prescribed but requires enhanced monitoring. [EVIDENCE: Moderate — ATA guidelines acknowledge some patients benefit. RCT data mixed.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // ARMOUR THYROID — rx-armour-thyroid
  // ================================================================

  {
    id: 'safety-armour-thyroid-cardiac',
    name: 'Armour Thyroid — Cardiac Risk (Contains T3)',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: [
      'atrial fibrillation', 'afib', 'cardiac arrhythmia', 'coronary artery disease',
      'angina', 'heart failure',
    ],
    affectedProductIds: ['rx-armour-thyroid'],
    message: 'Armour Thyroid (desiccated thyroid) contains both T4 and T3. The T3 component has direct cardiac effects and may exacerbate arrhythmias or angina. Use levothyroxine (T4 only) in cardiac patients.',
    clinicalNote: 'Armour Thyroid contains T4:T3 in ~4.2:1 ratio (each grain = 38mcg T4 + 9mcg T3), which is supraphysiological in T3 relative to human thyroid output (~14:1). The T3 content causes rapid cardiac stimulation. In patients with cardiac disease, the T3 peaks can trigger arrhythmias or ischemia. Levothyroxine monotherapy is preferred — it provides steady T4 with controlled peripheral T3 conversion. [EVIDENCE: Strong — pharmacological composition data. ATA guidelines recommend T4 monotherapy for cardiac patients.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-armour-thyroid-osteoporosis',
    name: 'Armour Thyroid — Bone Density Monitoring',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['osteoporosis', 'osteopenia', 'postmenopausal', 'low bone density'],
    affectedProductIds: ['rx-armour-thyroid'],
    message: 'Desiccated thyroid contains T3 which can cause intermittent hyperthyroid peaks. Monitor bone density annually in at-risk patients. Keep TSH ≥0.3.',
    clinicalNote: 'The supraphysiological T3:T4 ratio in Armour Thyroid means patients experience T3 peaks 2-4 hours post-dose. Even with normal TSH, intermittent T3 peaks may accelerate bone turnover. Postmenopausal women on desiccated thyroid are at particular risk. Monitor TSH and free T3 (timed to peak, 2-4h post-dose). DEXA at baseline and annually. [EVIDENCE: Moderate — extrapolated from T3 bone data. No dedicated Armour-specific bone density RCTs.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-armour-thyroid-duplicate',
    name: 'Armour Thyroid + Levothyroxine/Liothyronine — Duplicate',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-armour-thyroid', 'rx-levothyroxine'],
    affectedProductIds: ['rx-armour-thyroid', 'rx-levothyroxine'],
    message: 'Armour Thyroid already contains T4 and T3. Do not combine with levothyroxine or liothyronine unless intentionally adjusting the T4:T3 ratio under specialist guidance.',
    clinicalNote: 'Armour Thyroid provides both T4 (38mcg/grain) and T3 (9mcg/grain). Adding levothyroxine or liothyronine on top creates dosing complexity and risk of over-replacement. If a patient needs T4:T3 ratio adjustment, it is cleaner to use separate synthetic T4 + synthetic T3 at individualized doses rather than stacking Armour with synthetics. Choose one approach. [EVIDENCE: Strong — pharmacological principle. Endocrine society recommendations.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-armour-thyroid-duplicate-t3',
    name: 'Armour Thyroid + Liothyronine — Duplicate T3 Source',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-armour-thyroid', 'rx-liothyronine-t3'],
    affectedProductIds: ['rx-armour-thyroid', 'rx-liothyronine-t3'],
    message: 'Armour Thyroid already contains T3. Adding separate liothyronine creates excess T3 exposure and increased cardiac/bone risk. Choose one T3 source.',
    clinicalNote: 'Each grain of Armour Thyroid provides 9mcg T3. Adding exogenous liothyronine on top increases T3 peaks and may cause symptomatic thyrotoxicosis (palpitations, tremor, anxiety, bone loss). If patient needs more T3, increase Armour dose or switch to synthetic T4+T3 combination at calibrated doses. [EVIDENCE: Strong — pharmacological principle.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // TESTOSTERONE CREAM (FEMALE) — rx-testosterone-cream-female
  // ================================================================

  {
    id: 'safety-female-testosterone-pregnancy',
    name: 'Female Testosterone Cream — Pregnancy Contraindication',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'trying to conceive', 'planning pregnancy'],
    affectedProductIds: ['rx-testosterone-cream-female'],
    message: 'Testosterone is contraindicated in pregnancy. Exogenous androgens cause virilization of female fetuses (ambiguous genitalia, clitoromegaly).',
    clinicalNote: 'Testosterone crosses the placenta and causes dose-dependent virilization of female fetuses: clitoromegaly, labial fusion, ambiguous genitalia. Effects are irreversible. Discontinue testosterone cream before conception attempts. Effective contraception required during use. If pregnancy occurs, discontinue immediately. [EVIDENCE: Strong — well-characterized teratogenic effect. FDA Category X equivalent.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-female-testosterone-virilization',
    name: 'Female Testosterone Cream — Virilization Monitoring',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-testosterone-cream-female'],
    affectedProductIds: ['rx-testosterone-cream-female'],
    message: 'Monitor for signs of virilization: acne, hirsutism, voice deepening, clitoromegaly. Check total and free testosterone at 4-6 weeks. Target female physiological range.',
    clinicalNote: 'Female testosterone dosing (typically 0.5-1mg/day cream) aims for total T of 20-50 ng/dL (vs male 300-1000). Virilization occurs at supraphysiologic female levels (>70 ng/dL sustained). Early signs: acne, increased facial/body hair, oily skin. Later signs (often irreversible): voice deepening, clitoromegaly, male-pattern hair loss. Monitor total T, free T, and DHEA-S at 4-6 weeks, then q3-6 months. [EVIDENCE: Strong — well-characterized dose-response for virilization.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-female-testosterone-transfer',
    name: 'Female Testosterone Cream — Transfer Risk',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-testosterone-cream-female'],
    affectedProductIds: ['rx-testosterone-cream-female'],
    message: 'Topical testosterone can transfer to partners or children via skin contact. Apply to inner thigh or labia, cover with clothing, and wash hands thoroughly after application.',
    clinicalNote: 'Transdermal testosterone transfer via skin-to-skin contact is well-documented. Children exposed to adult testosterone cream have developed precocious puberty. Female partners of male TRT patients have reported virilization from contact transfer. Counsel on: application site selection (inner thigh/labia), covering application site, handwashing, and avoiding skin contact with others for 2+ hours. [EVIDENCE: Strong — FDA label warning. Multiple case reports of secondary exposure.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // DUTASTERIDE — rx-dutasteride
  // ================================================================

  {
    id: 'safety-dutasteride-pregnancy',
    name: 'Dutasteride — Pregnancy Category X',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'planning pregnancy', 'trying to conceive'],
    affectedProductIds: ['rx-dutasteride'],
    message: 'Dutasteride is Category X. Absolutely contraindicated in pregnancy. Even skin absorption of capsule contents can cause male fetal genital malformations. Extremely long half-life (5 weeks) — discontinue months before conception.',
    clinicalNote: 'Dutasteride inhibits both type I and type II 5-alpha reductase (vs finasteride type II only), more completely blocking DHT. Like finasteride, it causes hypospadias and genital malformations in male fetuses. CRITICAL: dutasteride has a 5-week half-life and is detectable in serum for up to 4-6 months after discontinuation. Women must not handle leaking capsules. Male patients should discontinue at least 6 months before partner conception attempts. Blood donation restricted for 6 months after last dose. [EVIDENCE: Strong — FDA Category X. PK data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-dutasteride-psa-masking',
    name: 'Dutasteride — PSA Masking (Prostate Cancer Screening)',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-dutasteride'],
    affectedProductIds: ['rx-dutasteride'],
    message: 'Dutasteride reduces PSA by approximately 50%. Double the measured PSA value for prostate cancer screening interpretation. Any confirmed PSA rise on dutasteride warrants urological evaluation.',
    clinicalNote: 'Dutasteride (and finasteride) reduce serum PSA by ~50% within 3-6 months. For prostate cancer screening, multiply measured PSA by 2 to estimate true PSA. A confirmed PSA increase of ≥0.3 ng/mL from nadir while on dutasteride may indicate prostate cancer even if the absolute value appears "normal." Establish new PSA baseline at 6 months on therapy. PCPT and REDUCE trials showed 5-ARI use associated with possible increase in high-grade prostate cancer detection (debated). [EVIDENCE: Strong — FDA label. REDUCE trial data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-dutasteride-duplicate-5ari',
    name: 'Dutasteride + Finasteride — Duplicate 5-ARI',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-dutasteride', 'rx-finasteride'],
    affectedProductIds: ['rx-dutasteride', 'rx-finasteride'],
    message: 'Dutasteride and finasteride are both 5-alpha reductase inhibitors. Do not combine. Dutasteride is more potent (dual inhibitor) — choose one.',
    clinicalNote: 'Dutasteride inhibits type I + II 5-alpha reductase (95% DHT reduction) vs finasteride type II only (70% DHT reduction). Combining both provides no additional benefit and increases side effect risk (sexual dysfunction, depression). If escalating from finasteride, switch to dutasteride — do not add on top. [EVIDENCE: Strong — pharmacological duplication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-dutasteride-blood-donation',
    name: 'Dutasteride — Blood Donation Restriction',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-dutasteride'],
    affectedProductIds: ['rx-dutasteride'],
    message: 'Do not donate blood while taking dutasteride and for 6 months after discontinuation due to risk of fetal exposure if blood is given to a pregnant recipient.',
    clinicalNote: 'Dutasteride is detectable in serum for 4-6 months after discontinuation (half-life ~5 weeks). Blood containing dutasteride could theoretically expose a pregnant transfusion recipient to a Category X teratogen. FDA label restricts blood donation during use and for 6 months post-discontinuation. Counsel patients accordingly. [EVIDENCE: Strong — FDA label restriction. PK data supports prolonged detection.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // NANDROLONE DECANOATE — rx-nandrolone-decanoate
  // ================================================================

  {
    id: 'safety-nandrolone-pregnancy',
    name: 'Nandrolone Decanoate — Pregnancy Category X',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'planning pregnancy', 'trying to conceive'],
    affectedProductIds: ['rx-nandrolone-decanoate'],
    message: 'Nandrolone is Category X — absolutely contraindicated in pregnancy. Causes virilization of female fetuses.',
    clinicalNote: 'Nandrolone (19-nortestosterone) is an anabolic-androgenic steroid that crosses the placenta. Causes virilization of female fetuses: clitoromegaly, labial fusion, voice changes. Nandrolone decanoate has a very long half-life (6-12 days IM) — discontinue well before conception attempts. [EVIDENCE: Strong — FDA Category X. Well-characterized androgenic teratogenicity.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-nandrolone-liver-toxicity',
    name: 'Nandrolone Decanoate — Hepatotoxicity Monitoring',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'cirrhosis', 'hepatitis'],
    affectedProductIds: ['rx-nandrolone-decanoate'],
    message: 'Anabolic steroids including nandrolone can cause hepatotoxicity including peliosis hepatis and hepatic neoplasms. Monitor LFTs at baseline and every 3 months.',
    clinicalNote: 'Anabolic steroids cause cholestatic hepatitis, peliosis hepatis (blood-filled liver cysts), and rarely hepatocellular carcinoma. While 17-alpha-alkylated orals (oxandrolone) are most hepatotoxic, injectable nandrolone retains hepatic risk, especially with prolonged use. LFTs at baseline, 6 weeks, then q3 months. If ALT/AST >3x ULN, discontinue. [EVIDENCE: Strong — FDA label. Post-marketing hepatotoxicity data for anabolic steroid class.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-nandrolone-cardiac-lipid',
    name: 'Nandrolone Decanoate — Cardiovascular and Lipid Risk',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-nandrolone-decanoate'],
    affectedProductIds: ['rx-nandrolone-decanoate'],
    message: 'Nandrolone adversely affects lipid profile (reduces HDL, increases LDL) and may cause fluid retention, edema, and cardiac hypertrophy. Monitor lipids and cardiac status.',
    clinicalNote: 'Anabolic steroids suppress HDL (often by 30-50%) and increase LDL, dramatically worsening the atherogenic lipid ratio. Additionally, fluid retention increases cardiac preload, and direct myocardial effects may cause LVH. Monitor: lipid panel q3 months, BMP (fluid/electrolytes), BP, and consider echocardiogram at baseline for long-term use. Existing cardiovascular disease is a relative contraindication. [EVIDENCE: Strong — well-characterized anabolic steroid cardiovascular effects. FDA label warnings.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-nandrolone-prostate',
    name: 'Nandrolone Decanoate — Prostate Cancer Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['prostate cancer', 'prostate carcinoma', 'elevated psa'],
    affectedProductIds: ['rx-nandrolone-decanoate'],
    message: 'Nandrolone is contraindicated in known or suspected prostate cancer. Anabolic steroids may accelerate prostate malignancy growth.',
    clinicalNote: 'Prostate cancer is androgen-responsive. While nandrolone has lower androgenic activity than testosterone, it and its metabolites still activate the androgen receptor. Known prostate cancer is an absolute contraindication. PSA screening before initiation in males >40. [EVIDENCE: Strong — FDA label contraindication. Androgen-receptor pharmacology.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-nandrolone-hematocrit',
    name: 'Nandrolone Decanoate — Polycythemia Monitoring',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-nandrolone-decanoate'],
    affectedProductIds: ['rx-nandrolone-decanoate'],
    message: 'Nandrolone stimulates erythropoiesis (historically used to treat anemia). Monitor hematocrit every 3 months. If >54%, phlebotomy required.',
    clinicalNote: 'Nandrolone was originally FDA-approved for anemia of chronic renal failure due to its potent erythropoietic stimulation. This effect persists at all doses. Monitor CBC at baseline, 6 weeks, then q3 months. Hematocrit >52%: dose reduction. >54%: therapeutic phlebotomy and consider discontinuation. Additive risk if combined with testosterone. [EVIDENCE: Strong — FDA-approved indication for anemia demonstrates potent erythropoietic effect.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // OXANDROLONE — rx-oxandrolone
  // ================================================================

  {
    id: 'safety-oxandrolone-pregnancy',
    name: 'Oxandrolone — Pregnancy Category X',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'planning pregnancy', 'trying to conceive'],
    affectedProductIds: ['rx-oxandrolone'],
    message: 'Oxandrolone is Category X — absolutely contraindicated in pregnancy. Causes virilization of female fetuses.',
    clinicalNote: 'Oxandrolone is a 17-alpha-alkylated anabolic steroid. Category X teratogen causing female fetal virilization. Shorter half-life (~9 hours) than nandrolone, but pregnancy is still an absolute contraindication. [EVIDENCE: Strong — FDA Category X.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-oxandrolone-liver-toxicity',
    name: 'Oxandrolone — Hepatotoxicity (17-Alpha-Alkylated)',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'cirrhosis', 'hepatitis', 'liver failure'],
    affectedProductIds: ['rx-oxandrolone'],
    message: 'Oxandrolone is a 17-alpha-alkylated oral steroid with significant hepatotoxicity potential. Contraindicated in existing liver disease. Monitor LFTs every 4 weeks.',
    clinicalNote: 'The 17-alpha-alkylation that allows oral bioavailability also causes hepatotoxicity: cholestatic jaundice, peliosis hepatis, hepatic adenoma, and rarely hepatocellular carcinoma. Oxandrolone is considered milder than other 17-alpha-alkylated steroids but retains class hepatotoxicity. LFTs at baseline, 2 weeks, 4 weeks, then monthly. ALT/AST >3x ULN: discontinue immediately. Existing liver disease is an absolute contraindication. [EVIDENCE: Strong — FDA label. Class effect well-documented.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-oxandrolone-lipid-effects',
    name: 'Oxandrolone — Severe Lipid Derangement',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-oxandrolone'],
    affectedProductIds: ['rx-oxandrolone'],
    message: 'Oxandrolone dramatically suppresses HDL cholesterol (often by 50%+) and elevates LDL. Monitor lipid panel monthly during use. Short treatment courses recommended.',
    clinicalNote: 'Oral anabolic steroids, particularly 17-alpha-alkylated compounds, cause severe HDL suppression via hepatic lipase induction. Oxandrolone can reduce HDL by 30-50% within 2 weeks. LDL elevation is also common. This dramatically increases atherogenic risk. Monitor lipids at baseline and monthly. Limit treatment duration. If HDL drops below 25 mg/dL, consider discontinuation. Co-prescribing with a statin does not fully mitigate this effect. [EVIDENCE: Strong — multiple clinical studies documenting lipid effects.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-oxandrolone-prostate',
    name: 'Oxandrolone — Prostate Cancer Contraindication',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['prostate cancer', 'prostate carcinoma'],
    affectedProductIds: ['rx-oxandrolone'],
    message: 'Oxandrolone is contraindicated in known or suspected prostate cancer. Androgen-receptor activation may accelerate tumor growth.',
    clinicalNote: 'All anabolic steroids activate the androgen receptor and are contraindicated in prostate cancer. PSA screening recommended in males >40 before initiation. Monitor PSA during treatment. [EVIDENCE: Strong — FDA label contraindication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-oxandrolone-hypercalcemia',
    name: 'Oxandrolone — Hypercalcemia Risk (Breast Cancer)',
    severity: 'block',
    triggerType: 'condition',
    matchConditions: ['breast cancer', 'hypercalcemia', 'bone metastases'],
    affectedProductIds: ['rx-oxandrolone'],
    message: 'Anabolic steroids may cause hypercalcemia in patients with breast cancer and bone metastases. Monitor calcium levels. Contraindicated in breast cancer with bone involvement.',
    clinicalNote: 'Anabolic steroids can stimulate osteolysis and increase serum calcium, particularly in patients with breast cancer bone metastases. Hypercalcemia symptoms: confusion, polyuria, constipation, cardiac arrhythmias. Monitor serum calcium at baseline and regularly. FDA label lists breast cancer with hypercalcemia as a contraindication. [EVIDENCE: Strong — FDA label contraindication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // CROSS-PRODUCT INTERACTIONS AND STACKING RULES
  // ================================================================

  {
    id: 'safety-anabolic-steroid-stacking',
    name: 'Multiple Anabolic Steroids — Stacking Warning',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-nandrolone-decanoate', 'rx-oxandrolone', 'rx-testosterone-cypionate'],
    affectedProductIds: ['rx-nandrolone-decanoate', 'rx-oxandrolone', 'rx-testosterone-cypionate'],
    message: 'Multiple anabolic-androgenic steroids detected. Stacking dramatically increases hepatotoxicity, cardiovascular risk, and hormonal suppression. Review clinical necessity of each agent.',
    clinicalNote: 'Anabolic steroid stacking compounds all risks: hepatotoxicity (especially with oral + injectable), lipid derangement (additive HDL suppression), polycythemia, cardiac hypertrophy, and HPG axis suppression. If multiple agents are clinically justified (rare in wellness context), mandate monthly LFTs, CBC, lipids, and cardiology clearance. Our protocol should rarely require more than one anabolic agent. [EVIDENCE: Strong — additive pharmacological risk. Clinical experience from endocrinology and sports medicine.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-serm-aromatase-inhibitor-conflict',
    name: 'Clomiphene/Enclomiphene + Anastrozole — Conflicting Mechanisms',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-clomiphene', 'rx-anastrozole'],
    affectedProductIds: ['rx-clomiphene', 'rx-anastrozole'],
    message: 'Clomiphene (SERM) and anastrozole (aromatase inhibitor) have overlapping estrogen-management mechanisms. Combined use is generally not recommended — choose one approach.',
    clinicalNote: 'Clomiphene blocks estrogen at the hypothalamus to increase GnRH/LH/FSH. Anastrozole blocks estrogen production at aromatase. Combined use creates unpredictable estrogen dynamics. In male hypogonadism, choose one: clomiphene/enclomiphene (to stimulate endogenous T production) OR TRT + anastrozole (to manage TRT-induced estrogen). Using both simultaneously lacks clinical rationale. [EVIDENCE: Moderate — pharmacological conflict. No clinical data supporting combination.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-enclomiphene-anastrozole-conflict',
    name: 'Enclomiphene + Anastrozole — Conflicting Mechanisms',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-enclomiphene', 'rx-anastrozole'],
    affectedProductIds: ['rx-enclomiphene', 'rx-anastrozole'],
    message: 'Enclomiphene (SERM) and anastrozole (AI) both modify estrogen signaling. Combined use is rarely justified. Choose one estrogen-management strategy.',
    clinicalNote: 'Same rationale as clomiphene + anastrozole. Enclomiphene blocks estrogen at hypothalamic ER to increase LH. Anastrozole reduces estrogen production. The combination excessively suppresses estrogen activity which can cause bone loss, lipid worsening, joint pain, and mood changes. If male patient needs both gonadotropin stimulation and estrogen control, use enclomiphene alone (it provides both effects). [EVIDENCE: Moderate — pharmacological reasoning.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-serm-with-trt',
    name: 'Clomiphene/Enclomiphene + TRT — Conflicting Approach',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-clomiphene', 'rx-testosterone-cypionate'],
    affectedProductIds: ['rx-clomiphene', 'rx-testosterone-cypionate'],
    message: 'Clomiphene stimulates endogenous testosterone production via LH/FSH. Exogenous testosterone (TRT) suppresses LH/FSH, negating clomiphene mechanism. Choose one approach.',
    clinicalNote: 'Clomiphene works by blocking hypothalamic estrogen receptors, increasing GnRH → LH/FSH → testicular testosterone. Exogenous testosterone suppresses GnRH/LH/FSH via negative feedback, making clomiphene ineffective. These are opposing strategies. Use clomiphene as an alternative to TRT (preserves fertility/testicular function) OR use TRT (more reliable testosterone levels). Do not combine. Exception: brief overlap during transition from clomiphene to TRT is acceptable. [EVIDENCE: Strong — HPG axis pharmacology.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-enclomiphene-with-trt',
    name: 'Enclomiphene + TRT — Conflicting Approach',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-enclomiphene', 'rx-testosterone-cypionate'],
    affectedProductIds: ['rx-enclomiphene', 'rx-testosterone-cypionate'],
    message: 'Enclomiphene stimulates endogenous testosterone — exogenous TRT suppresses this pathway. Choose one approach. Exception: brief overlap during transition is acceptable.',
    clinicalNote: 'Same pharmacological conflict as clomiphene + TRT. Enclomiphene increases LH/FSH to stimulate Leydig cells. Exogenous testosterone suppresses LH/FSH. Combination is pharmacologically self-defeating. Choose enclomiphene (preserves fertility, testicular volume) or TRT (higher, more reliable T levels). [EVIDENCE: Strong — HPG axis pharmacology.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-hcg-with-serm',
    name: 'HCG + Clomiphene/Enclomiphene — Combined Gonadal Stimulation',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-hcg', 'rx-clomiphene'],
    affectedProductIds: ['rx-hcg', 'rx-clomiphene'],
    message: 'HCG (LH analog) and clomiphene (increases endogenous LH/FSH) both stimulate gonadal function. Combination is sometimes used but monitor estradiol closely — both increase intratesticular estrogen production.',
    clinicalNote: 'HCG directly stimulates Leydig cells (LH receptor agonist). Clomiphene increases endogenous LH/FSH. Combined use provides additive gonadal stimulation, which may be useful in severe hypogonadism or fertility preservation. However, both increase intratesticular aromatase activity and estradiol production. Monitor E2 at 4 weeks. Gynecomastia risk is elevated. [EVIDENCE: Moderate — used in clinical practice. Limited RCT data for combination.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-glp1-oral-contraceptives',
    name: 'GLP-1 Agents — Oral Contraceptive Absorption Warning',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'oral contraceptive', 'birth control pill', 'ethinyl estradiol',
      'levonorgestrel', 'norgestimate', 'drospirenone', 'desogestrel',
    ],
    affectedProductIds: ['rx-semaglutide', 'rx-tirzepatide'],
    message: 'GLP-1 agents delay gastric emptying, which may reduce oral contraceptive absorption. Counsel patients to use backup contraception during GLP-1 initiation and dose escalation.',
    clinicalNote: 'Delayed gastric emptying from GLP-1 RAs may alter oral contraceptive pharmacokinetics. Tirzepatide FDA label specifically notes reduced oral contraceptive exposure. Recommend backup contraception (barrier method) during initiation, dose escalation, and for 4 weeks after reaching stable dose. Consider switching to non-oral contraception (IUD, implant, patch) for patients on long-term GLP-1 therapy. [EVIDENCE: Moderate — tirzepatide label cites PK data. Semaglutide PK interaction studies show modest effect.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-sermorelin-duplicate-gh',
    name: 'Sermorelin + Other GH Secretagogues — Duplicate',
    severity: 'warn',
    triggerType: 'duplicate',
    matchProductIds: ['rx-sermorelin'],
    affectedProductIds: ['rx-sermorelin'],
    message: 'If combining sermorelin with other GH-releasing peptides (ipamorelin, CJC-1295, etc.), monitor IGF-1 closely. Excessive GH stimulation increases diabetes and cancer risk.',
    clinicalNote: 'Multiple GH secretagogues provide additive GH release. While sermorelin (GHRH analog) and ghrelin mimetics (ipamorelin, GHRP-6) work through different receptors, the combined GH output may exceed physiological replacement goals. Monitor IGF-1 at 4-6 weeks — target upper quartile of age-adjusted range, not supraphysiological. IGF-1 >1.5x ULN: reduce doses. [EVIDENCE: Moderate — pharmacological rationale. Limited clinical stacking data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-bupropion-wellbutrin-duplicate',
    name: 'Bupropion — Duplicate Prescribing Warning',
    severity: 'warn',
    triggerType: 'duplicate',
    matchMedications: ['bupropion', 'wellbutrin', 'zyban', 'aplenzin', 'forfivo'],
    affectedProductIds: ['rx-bupropion'],
    message: 'Patient may already be taking bupropion from another provider. Verify before prescribing to avoid duplicate therapy. Combined dosing exceeds 450mg/day seizure threshold.',
    clinicalNote: 'Bupropion is marketed under multiple brand names for different indications: Wellbutrin (depression), Zyban (smoking cessation), Aplenzin (depression — bupropion hydrobromide). Patients may not recognize these as the same drug. Total daily bupropion from all sources must not exceed 450mg. Medication reconciliation is critical. [EVIDENCE: Strong — FDA label dose ceiling. Known duplicate-prescribing issue.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-dhea-female-androgenic',
    name: 'DHEA in Females — Androgenic Side Effects',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-dhea'],
    affectedProductIds: ['rx-dhea'],
    message: 'DHEA converts to testosterone and may cause androgenic side effects in women (acne, hirsutism, hair loss). Use lower doses (5-10mg) in females and monitor androgen levels.',
    clinicalNote: 'Female DHEA dosing should be 5-25mg/day (vs male 25-50mg). DHEA-S, total testosterone, and free testosterone should be monitored at 4-6 weeks. DHEA-S target: upper quartile of age-adjusted female range. If testosterone exceeds female physiological range (>50 ng/dL), reduce dose. Androgenic side effects: acne, oily skin, hirsutism, female pattern hair loss. [EVIDENCE: Moderate — clinical experience and observational data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-pregnenolone-hormone-stacking-estradiol',
    name: 'Pregnenolone + Estradiol — Downstream Hormone Excess',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-pregnenolone', 'rx-estradiol-patch'],
    affectedProductIds: ['rx-pregnenolone', 'rx-estradiol-patch'],
    message: 'Pregnenolone converts downstream to estradiol. Combined with exogenous estradiol, total estrogen exposure may exceed target. Monitor estradiol levels.',
    clinicalNote: 'Pregnenolone → DHEA → androstenedione → estrone → estradiol. While conversion efficiency is modest, adding pregnenolone on top of exogenous estradiol increases total estrogen load. Monitor E2 at 6 weeks. Particularly relevant for breast cancer risk assessment. [EVIDENCE: Moderate — steroidogenesis pathway. Limited clinical data on combined use.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-minoxidil-pregnancy',
    name: 'Oral Minoxidil — Pregnancy Contraindication',
    severity: 'block',
    triggerType: 'pregnancy',
    matchConditions: ['pregnant', 'pregnancy', 'planning pregnancy', 'trying to conceive'],
    affectedProductIds: ['rx-minoxidil-oral'],
    message: 'Oral minoxidil is contraindicated in pregnancy. May cause fetal adverse effects including hypertrichosis and cardiovascular changes.',
    clinicalNote: 'Minoxidil crosses the placenta. Animal studies show adverse fetal effects. Hypertrichosis (excess hair growth) reported in neonates exposed in utero. Vasodilatory effects may impact fetal hemodynamics. Discontinue before conception. Use effective contraception during treatment. [EVIDENCE: Moderate — animal data. Limited human pregnancy exposure data but precautionary block is appropriate.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-buspirone-hepatic-renal',
    name: 'Buspirone — Hepatic/Renal Impairment Dose Adjustment',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['liver disease', 'hepatic impairment', 'renal impairment', 'kidney disease', 'ckd'],
    affectedProductIds: ['rx-buspirone'],
    message: 'Buspirone is hepatically metabolized and renally excreted. Reduce dose in hepatic or renal impairment. Contraindicated in severe hepatic/renal failure.',
    clinicalNote: 'Buspirone undergoes extensive first-pass hepatic metabolism (CYP3A4). In hepatic impairment, bioavailability increases and clearance decreases. In renal impairment (CrCl <10 mL/min), metabolite accumulation occurs. Reduce dose by 50% in mild-moderate hepatic/renal impairment. Avoid in severe impairment. Monitor for excessive sedation. [EVIDENCE: Strong — FDA label PK data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-ezetimibe-cyclosporine',
    name: 'Ezetimibe + Cyclosporine — Elevated Levels',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['cyclosporine', 'sandimmune', 'neoral', 'gengraf'],
    affectedProductIds: ['rx-ezetimibe'],
    message: 'Cyclosporine increases ezetimibe levels significantly. Monitor for myopathy and consider dose reduction. If statin is also co-prescribed, myopathy risk is further elevated.',
    clinicalNote: 'Cyclosporine increases ezetimibe Cmax by 3.4-fold and AUC by 3.4-fold. Ezetimibe also increases cyclosporine levels. If combination is necessary, monitor cyclosporine levels closely and watch for myopathy symptoms, especially if a statin is also in the regimen. [EVIDENCE: Strong — FDA label PK interaction data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-nandrolone-anticoagulants',
    name: 'Nandrolone + Anticoagulants — Enhanced Bleeding Risk',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['warfarin', 'coumadin', 'enoxaparin', 'lovenox', 'apixaban', 'eliquis', 'rivaroxaban', 'xarelto'],
    affectedProductIds: ['rx-nandrolone-decanoate'],
    message: 'Anabolic steroids can enhance anticoagulant effect and increase bleeding risk. Monitor INR/coagulation parameters closely and adjust anticoagulant dosing.',
    clinicalNote: 'Anabolic steroids potentiate anticoagulant effects via multiple mechanisms: reduced clotting factor synthesis, increased fibrinolysis, and altered platelet function. Warfarin dose reduction of 30-50% may be needed. Monitor INR weekly during nandrolone initiation and dose changes. For DOACs, monitor for clinical signs of bleeding. [EVIDENCE: Strong — FDA label. Well-documented interaction for anabolic steroid class.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-oxandrolone-anticoagulants',
    name: 'Oxandrolone + Anticoagulants — Enhanced Bleeding Risk',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['warfarin', 'coumadin', 'enoxaparin', 'lovenox', 'apixaban', 'eliquis', 'rivaroxaban', 'xarelto'],
    affectedProductIds: ['rx-oxandrolone'],
    message: 'Oxandrolone potentiates warfarin anticoagulation. Reduce warfarin dose and monitor INR frequently when initiating or discontinuing oxandrolone.',
    clinicalNote: 'Oxandrolone is specifically noted for potent warfarin interaction. May increase INR dramatically. When adding oxandrolone to warfarin therapy, reduce warfarin dose by ~33% preemptively and check INR at 3-5 days, then weekly for the first month. Same caution applies to other anticoagulants. [EVIDENCE: Strong — FDA label. Case reports of supratherapeutic INR.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-oxandrolone-diabetes',
    name: 'Oxandrolone — Glucose Lowering in Diabetics',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['diabetes', 'type 2 diabetes', 'type 1 diabetes'],
    affectedProductIds: ['rx-oxandrolone'],
    message: 'Oxandrolone may enhance insulin sensitivity and lower blood glucose. Diabetic patients on insulin or sulfonylureas may require dose reduction to avoid hypoglycemia.',
    clinicalNote: 'Anabolic steroids improve insulin sensitivity via increased muscle mass and direct metabolic effects. Oxandrolone has documented glucose-lowering properties. In diabetic patients on insulin or sulfonylureas, hypoglycemia risk increases. Monitor glucose closely during initiation. Reduce insulin dose by 10-20% preemptively if starting oxandrolone. [EVIDENCE: Moderate — clinical studies in burn patients document glucose effects. FDA label mentions diabetes.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-bupropion-ssri-serotonin',
    name: 'Bupropion + SSRIs — Combined Antidepressant Monitoring',
    severity: 'info',
    triggerType: 'drug_interaction',
    matchMedications: [
      'fluoxetine', 'sertraline', 'paroxetine', 'citalopram',
      'escitalopram', 'fluvoxamine',
    ],
    affectedProductIds: ['rx-bupropion'],
    message: 'Bupropion + SSRI combination is commonly used (augmentation). However, bupropion inhibits CYP2D6, which may increase levels of some SSRIs (paroxetine, fluoxetine). Monitor for side effects.',
    clinicalNote: 'Bupropion augmentation of SSRIs is evidence-based for treatment-resistant depression (STAR*D trial). However, bupropion CYP2D6 inhibition increases paroxetine and fluoxetine levels (both CYP2D6 substrates). Sertraline and citalopram are less affected. Theoretical seizure risk is additive but clinically uncommon at standard doses. This combination is valid but warrants monitoring. [EVIDENCE: Strong — STAR*D trial. PK interaction data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-spironolactone-ace-arb-potassium',
    name: 'Spironolactone + ACEi/ARB — Severe Hyperkalemia',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'lisinopril', 'enalapril', 'ramipril', 'benazepril', 'losartan',
      'valsartan', 'irbesartan', 'candesartan', 'olmesartan',
    ],
    affectedProductIds: ['rx-spironolactone'],
    message: 'Spironolactone combined with ACE inhibitors or ARBs dramatically increases hyperkalemia risk. Monitor potassium within 1 week of initiation and monthly thereafter.',
    clinicalNote: 'Triple RAAS blockade (spironolactone + ACEi/ARB) is the highest-risk combination for hyperkalemia. Both ACEi/ARBs and spironolactone independently reduce potassium excretion. RALES trial showed benefit in heart failure but with strict potassium monitoring. In our wellness context (spironolactone for anti-androgen/acne in females), this combination warrants BMP at 1 week, 4 weeks, then monthly. K+ >5.5: hold spironolactone. [EVIDENCE: Strong — RALES trial. Multiple studies document hyperkalemia risk.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-sildenafil-pulmonary-hypertension',
    name: 'Sildenafil — Concurrent PAH Use Check',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['revatio', 'sildenafil 20mg'],
    affectedProductIds: ['rx-sildenafil'],
    message: 'If patient is already on sildenafil for pulmonary arterial hypertension (Revatio), do not add sildenafil for ED. Total sildenafil dose from all sources must be managed.',
    clinicalNote: 'Sildenafil is marketed as both Viagra (ED, 25-100mg PRN) and Revatio (PAH, 20mg TID). Patients on Revatio already receive 60mg/day of sildenafil. Adding Viagra creates potential overdose and severe hypotension. Medication reconciliation must identify all sildenafil sources. Consider tadalafil (Adcirca/Cialis) if separate PAH and ED management is needed but doses must be coordinated. [EVIDENCE: Strong — FDA label. Known duplicate-prescribing risk.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-sermorelin-hypothyroidism',
    name: 'Sermorelin — May Unmask Hypothyroidism',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-sermorelin'],
    affectedProductIds: ['rx-sermorelin'],
    message: 'GH therapy can unmask central hypothyroidism by increasing T4-to-T3 conversion and clearance. Monitor thyroid function (TSH, free T4) at baseline and 3 months after sermorelin initiation.',
    clinicalNote: 'Growth hormone increases peripheral conversion of T4 to T3 and may increase T4 clearance, unmasking subclinical central hypothyroidism. Patients may develop fatigue, cold intolerance, or weight gain after starting GH-releasing agents. Check TSH and free T4 at baseline and 3 months. If new hypothyroidism develops, add levothyroxine. [EVIDENCE: Moderate — documented with GH replacement therapy. Applicable to sermorelin-stimulated GH release.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-clomiphene-ovarian-hyperstim',
    name: 'Clomiphene — Ovarian Hyperstimulation in Females',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-clomiphene'],
    affectedProductIds: ['rx-clomiphene'],
    message: 'Clomiphene in females may cause ovarian enlargement and hyperstimulation. Multiple gestations risk increases. Ovarian monitoring recommended during use.',
    clinicalNote: 'Clomiphene stimulates multiple follicle development. Ovarian hyperstimulation syndrome (OHSS) is rare at standard doses but possible. Multiple gestations occur in ~8% of clomiphene cycles (mostly twins). In our formulary, clomiphene is primarily for male hypogonadism. If prescribed to females, ensure fertility specialist oversight, ultrasound monitoring, and informed consent regarding multiple pregnancy risk. [EVIDENCE: Strong — decades of fertility medicine data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-minoxidil-pericardial-effusion',
    name: 'Oral Minoxidil — Pericardial Effusion Monitoring',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-minoxidil-oral'],
    affectedProductIds: ['rx-minoxidil-oral'],
    message: 'Oral minoxidil can cause pericardial effusion even at low doses used for hair loss. Report dyspnea, chest pain, or new cardiac symptoms immediately. Baseline ECG recommended.',
    clinicalNote: 'Pericardial effusion is a known adverse effect of minoxidil, reported even at low doses (2.5-5mg) used for hair loss. Mechanism: likely direct vasodilatory effect on pericardial vasculature. Most cases are small and asymptomatic, but tamponade has been reported at higher doses. Baseline ECG recommended. Repeat ECG or echocardiogram if symptoms develop. Incidence at hair-loss doses is rare but non-zero. [EVIDENCE: Moderate — FDA black box at antihypertensive doses. Case reports at low doses.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-tirzepatide-gallbladder',
    name: 'Tirzepatide — Gallbladder Events',
    severity: 'warn',
    triggerType: 'condition',
    matchConditions: ['gallstones', 'cholelithiasis', 'gallbladder disease', 'cholecystitis'],
    affectedProductIds: ['rx-tirzepatide', 'rx-semaglutide'],
    message: 'GLP-1 agents increase risk of cholelithiasis and cholecystitis, especially with rapid weight loss. Monitor for RUQ pain, nausea after meals.',
    clinicalNote: 'Rapid weight loss from GLP-1 agents increases bile cholesterol saturation and gallstone formation. SURMOUNT and SURPASS trials showed increased cholelithiasis rates. Risk factors: female sex, obesity, rapid weight loss (>1.5 kg/week), history of gallstones. Counsel patients on gallbladder symptoms. Ursodiol prophylaxis may be considered in high-risk patients. [EVIDENCE: Strong — clinical trial data from SURMOUNT/SURPASS. Well-known rapid-weight-loss complication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-nandrolone-diabetes',
    name: 'Nandrolone — Glucose Effects in Diabetics',
    severity: 'info',
    triggerType: 'condition',
    matchConditions: ['diabetes', 'type 2 diabetes'],
    affectedProductIds: ['rx-nandrolone-decanoate'],
    message: 'Nandrolone may improve insulin sensitivity and lower glucose requirements. Monitor blood glucose and adjust diabetes medications to avoid hypoglycemia.',
    clinicalNote: 'Anabolic steroids increase lean muscle mass and may improve insulin sensitivity. Diabetic patients on nandrolone may require insulin or sulfonylurea dose reduction. Monitor glucose closely for 4-6 weeks after initiation. [EVIDENCE: Moderate — FDA label mentions diabetes. Clinical data from wasting syndrome studies.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-dhea-anticoagulants',
    name: 'DHEA + Anticoagulants — Potential Interaction',
    severity: 'info',
    triggerType: 'drug_interaction',
    matchMedications: ['warfarin', 'coumadin'],
    affectedProductIds: ['rx-dhea'],
    message: 'DHEA may alter anticoagulant sensitivity. Monitor INR more frequently when initiating DHEA in patients on warfarin.',
    clinicalNote: 'Limited case reports suggest DHEA may potentiate warfarin effect via uncertain mechanism (possibly hepatic metabolism competition or hormonal effects on clotting factors). Monitor INR at 1 and 4 weeks after DHEA initiation. Clinical significance is uncertain but warrants awareness. [EVIDENCE: Limited — case reports only. Precautionary monitoring recommended.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-hcg-duplicate-gonadotropin',
    name: 'HCG + TRT — Intentional Combination Monitoring',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-hcg', 'rx-testosterone-cypionate'],
    affectedProductIds: ['rx-hcg', 'rx-testosterone-cypionate'],
    message: 'HCG as TRT adjunct is a valid protocol (maintains testicular volume and fertility). Monitor estradiol closely — both exogenous T and HCG-stimulated T contribute to aromatization.',
    clinicalNote: 'HCG (250-500 IU 2-3x/week) as TRT adjunct prevents testicular atrophy and maintains spermatogenesis. This is a valid and common protocol. However, HCG stimulates intratesticular testosterone AND estradiol production. Combined with aromatization of exogenous testosterone, estradiol may rise significantly. Monitor E2 at 4-6 weeks. Anastrozole may be needed at lower threshold than TRT alone. [EVIDENCE: Moderate — clinical practice standard. Limited RCT data for combination.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-anastrozole-depression',
    name: 'Anastrozole — Mood and Cognitive Effects',
    severity: 'info',
    triggerType: 'combination',
    matchProductIds: ['rx-anastrozole'],
    affectedProductIds: ['rx-anastrozole'],
    message: 'Estrogen suppression from anastrozole may cause mood changes, depression, cognitive fog, and decreased libido. Monitor mood and adjust dosing to maintain E2 in therapeutic range (males: 20-35 pg/mL).',
    clinicalNote: 'Estrogen has neuroprotective and mood-stabilizing effects in both sexes. Over-suppression with anastrozole (E2 <15 pg/mL in males) commonly causes fatigue, low mood, joint pain, decreased libido, and cognitive impairment. The goal is estrogen management, not elimination. Target male E2: 20-35 pg/mL. Dose anastrozole conservatively (0.25-0.5mg 2x/week, not daily). Assess mood at each follow-up. [EVIDENCE: Moderate — clinical experience. Breast cancer adjuvant trial QOL data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-female-testosterone-with-dhea',
    name: 'Female Testosterone Cream + DHEA — Androgen Excess Risk',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-testosterone-cream-female', 'rx-dhea'],
    affectedProductIds: ['rx-testosterone-cream-female', 'rx-dhea'],
    message: 'Both testosterone cream and DHEA raise androgen levels in women. Combined use increases virilization risk. Monitor total and free testosterone closely.',
    clinicalNote: 'DHEA converts to testosterone (and estradiol) in women. When combined with exogenous testosterone cream, total androgen exposure may exceed female physiological range, causing acne, hirsutism, voice deepening. Start with one agent, optimize, then consider adding the second only if clinically necessary. Monitor total T, free T, DHEA-S at 4-6 weeks if both are prescribed. [EVIDENCE: Moderate — pharmacological additive effect. Clinical experience.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // TADALAFIL — rx-tadalafil
  // ================================================================

  {
    id: 'safety-tadalafil-alpha-blockers',
    name: 'Tadalafil + Alpha-Blockers — Orthostatic Hypotension',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'tamsulosin', 'flomax', 'alfuzosin', 'uroxatral', 'doxazosin',
      'cardura', 'terazosin', 'silodosin', 'rapaflo', 'prazosin',
    ],
    affectedProductIds: ['rx-tadalafil'],
    message: 'Tadalafil combined with alpha-adrenergic blockers increases risk of symptomatic orthostatic hypotension. Start tadalafil at lowest dose. Separate dosing times if possible.',
    clinicalNote: 'PDE5 inhibitors and alpha-blockers both cause vasodilation. Tadalafil FDA label warns of orthostatic hypotension with alpha-blockers. Tamsulosin is the least likely to cause additive hypotension (uroselective alpha-1A). Start tadalafil at 5mg when used with alpha-blockers. Counsel on position changes. [EVIDENCE: Strong. FDA label warning based on clinical interaction studies.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // FINASTERIDE — rx-finasteride
  // ================================================================

  {
    id: 'safety-finasteride-psa-masking',
    name: 'Finasteride — PSA Screening Masking',
    severity: 'warn',
    triggerType: 'combination',
    matchProductIds: ['rx-finasteride'],
    affectedProductIds: ['rx-finasteride'],
    message: 'Finasteride reduces PSA levels by approximately 50%. Any PSA value in a patient on finasteride must be doubled for accurate prostate cancer screening interpretation.',
    clinicalNote: 'Finasteride inhibits type II 5-alpha reductase, reducing intraprostatic DHT and consequently PSA production by ~50% after 6+ months of use. A PSA of 2.0 ng/mL in a man on finasteride is functionally equivalent to 4.0 ng/mL — above the screening threshold. Failure to adjust PSA values may result in missed prostate cancer diagnosis. AUA and NCCN guidelines recommend doubling measured PSA values for patients on 5-alpha reductase inhibitors. [EVIDENCE: Strong. Well-established in clinical guidelines.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // ORAL MINOXIDIL + PDE5 INHIBITORS — Severe Hypotension
  // ================================================================

  {
    id: 'safety-minoxidil-pde5',
    name: 'Oral Minoxidil + PDE5 Inhibitors — Severe Hypotension Risk',
    severity: 'block',
    triggerType: 'duplicate',
    matchProductIds: ['rx-minoxidil-oral', 'rx-tadalafil'],
    affectedProductIds: ['rx-minoxidil-oral', 'rx-tadalafil'],
    message: 'BLOCKED: Oral minoxidil (potent vasodilator) combined with PDE5 inhibitors (tadalafil, sildenafil) may cause severe additive hypotension. This combination requires cardiology clearance.',
    clinicalNote: 'Oral minoxidil opens potassium channels causing arteriolar vasodilation. PDE5 inhibitors cause vasodilation via NO-cGMP pathway. Combined vasodilation may cause precipitous BP drop, syncope, or end-organ hypoperfusion. Hair loss patients (minoxidil) and ED patients (PDE5i) overlap demographically. If both are clinically needed, start at lowest doses with BP monitoring. Cardiology clearance required. [EVIDENCE: Strong mechanistic rationale. Case reports of severe hypotension with combined vasodilators.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  {
    id: 'safety-minoxidil-pde5-sildenafil',
    name: 'Oral Minoxidil + Sildenafil — Severe Hypotension Risk',
    severity: 'block',
    triggerType: 'duplicate',
    matchProductIds: ['rx-minoxidil-oral', 'rx-sildenafil'],
    affectedProductIds: ['rx-minoxidil-oral', 'rx-sildenafil'],
    message: 'BLOCKED: Oral minoxidil (potent vasodilator) combined with sildenafil may cause severe additive hypotension. This combination requires cardiology clearance.',
    clinicalNote: 'Same mechanism as tadalafil interaction. Sildenafil has shorter duration (4-6 hours vs 36 hours for tadalafil) but hemodynamic risk is equivalent during active effect window. [EVIDENCE: Strong mechanistic rationale.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // ARMOUR THYROID + IRON — Timing Separation
  // ================================================================

  {
    id: 'safety-armour-thyroid-iron-timing',
    name: 'Armour Thyroid + Iron — 4-Hour Separation Required',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchProductIds: ['rx-armour-thyroid', 'supp-iron-bisglycinate'],
    affectedProductIds: ['rx-armour-thyroid', 'supp-iron-bisglycinate'],
    message: 'Iron supplements reduce thyroid hormone absorption by 30-50% via chelation. Take Armour Thyroid on empty stomach AM; iron supplements at least 4 hours later.',
    clinicalNote: 'Same chelation interaction as levothyroxine + iron (seed rule safety-thyroid-iron-timing). Armour Thyroid contains both T4 and T3 — both are chelated by iron in the GI tract. The same 4-hour separation applies. Also applies to calcium and coffee. [EVIDENCE: Strong. Same mechanism as levothyroxine interaction, well-documented.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // LIOTHYRONINE (T3) + IRON — Timing Separation
  // ================================================================

  {
    id: 'safety-liothyronine-iron-timing',
    name: 'Liothyronine (T3) + Iron — 4-Hour Separation Required',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchProductIds: ['rx-liothyronine-t3', 'supp-iron-bisglycinate'],
    affectedProductIds: ['rx-liothyronine-t3', 'supp-iron-bisglycinate'],
    message: 'Iron supplements reduce T3 absorption via chelation. Take liothyronine on empty stomach; iron at least 4 hours later.',
    clinicalNote: 'T3 (liothyronine) is chelated by iron in the GI tract similar to T4. 4-hour separation required. [EVIDENCE: Strong. Same mechanism as T4/iron interaction.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // GLP-1 AGONISTS + INSULIN/SULFONYLUREAS — Hypoglycemia
  // ================================================================

  {
    id: 'safety-glp1-insulin-hypoglycemia',
    name: 'GLP-1 Agonists + Insulin/Sulfonylureas — Hypoglycemia Risk',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'insulin', 'glargine', 'lantus', 'basaglar', 'detemir', 'levemir',
      'degludec', 'tresiba', 'lispro', 'humalog', 'aspart', 'novolog',
      'glipizide', 'glucotrol', 'glyburide', 'glimepiride', 'amaryl',
    ],
    affectedProductIds: ['rx-semaglutide', 'rx-tirzepatide'],
    message: 'GLP-1 agonists combined with insulin or sulfonylureas significantly increase hypoglycemia risk. Insulin/sulfonylurea dose reduction (typically 20-50%) is recommended when initiating GLP-1 therapy.',
    clinicalNote: 'GLP-1 RAs potentiate glucose-dependent insulin secretion. When combined with exogenous insulin or sulfonylureas (glucose-independent insulin secretagogues), hypoglycemia risk increases substantially. ADA guidelines recommend: reduce sulfonylurea dose by 50% when adding GLP-1 RA. Reduce basal insulin by 20% when adding GLP-1 RA. Monitor glucose closely for 2-4 weeks. [EVIDENCE: Strong. Multiple RCTs and clinical guidelines.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // TESTOSTERONE CYPIONATE + ANTICOAGULANTS — Enhanced Effect
  // ================================================================

  {
    id: 'safety-testosterone-anticoagulants',
    name: 'Testosterone Cypionate + Anticoagulants — Enhanced Effect',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'warfarin', 'coumadin', 'apixaban', 'eliquis', 'rivaroxaban', 'xarelto',
    ],
    affectedProductIds: ['rx-testosterone-cypionate'],
    message: 'Testosterone enhances anticoagulant effects, particularly warfarin. INR monitoring required. Testosterone also increases hematocrit (polycythemia risk), adding further thrombotic complexity in anticoagulated patients.',
    clinicalNote: 'Testosterone increases erythropoiesis (polycythemia risk) while also enhancing warfarin anticoagulation (decreased clotting factor synthesis). This creates a dual risk: bleeding from enhanced anticoagulation AND thrombotic events from polycythemia. FDA label for testosterone products warns of increased anticoagulant effect. If patient is on warfarin: increase INR monitoring to weekly for first month, then biweekly. For DOACs: monitor hematocrit q3mo and clinical signs of bleeding. [EVIDENCE: Strong. FDA label warning. Multiple case reports of INR elevation.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // EZETIMIBE + WARFARIN — INR Monitoring
  // ================================================================

  {
    id: 'safety-ezetimibe-warfarin',
    name: 'Ezetimibe + Warfarin — INR Monitoring',
    severity: 'info',
    triggerType: 'drug_interaction',
    matchMedications: ['warfarin', 'coumadin'],
    affectedProductIds: ['rx-ezetimibe'],
    message: 'Ezetimibe may cause mild INR elevation in patients on warfarin. Monitor INR when initiating or changing ezetimibe dose.',
    clinicalNote: 'Post-marketing reports of increased INR in patients taking ezetimibe with warfarin. Mechanism unclear — possibly protein binding displacement or altered vitamin K absorption. Effect is generally mild but clinically relevant in patients with tight INR targets. Check INR within 1-2 weeks of starting ezetimibe. [EVIDENCE: Moderate. Post-marketing reports. FDA label mentions interaction.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // PT-141 (BREMELANOTIDE) + ANTIHYPERTENSIVES — BP Effects
  // ================================================================

  {
    id: 'safety-pt141-antihypertensives',
    name: 'PT-141 (Bremelanotide) + Antihypertensives — BP Effects',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: [
      'lisinopril', 'enalapril', 'losartan', 'valsartan', 'amlodipine',
      'metoprolol', 'atenolol', 'hydrochlorothiazide', 'clonidine',
    ],
    affectedProductIds: ['rx-pt141-bremelanotide'],
    message: 'PT-141 (bremelanotide) causes transient blood pressure increases. In patients on antihypertensives, monitor BP. Avoid in uncontrolled hypertension (>170/100).',
    clinicalNote: 'Bremelanotide causes transient dose-dependent BP elevation (avg +6/3 mmHg systolic/diastolic, onset ~4 hours post-dose, duration ~12 hours) and heart rate decrease (~5 bpm). FDA label contraindicates use in uncontrolled hypertension. In patients on antihypertensives, the transient pressor effect may overcome medication effects. Monitor BP. Do not use if baseline BP >170/100. [EVIDENCE: Strong. FDA label based on Phase 3 clinical trial data.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

  // ================================================================
  // BUPROPION + ALCOHOL — Seizure Risk
  // ================================================================

  {
    id: 'safety-bupropion-alcohol',
    name: 'Bupropion + Heavy Alcohol Use — Seizure Risk',
    severity: 'warn',
    triggerType: 'drug_interaction',
    matchMedications: ['alcohol', 'ethanol'],
    matchConditions: ['heavy alcohol use', 'alcohol use disorder', 'alcoholism', 'heavy drinking'],
    affectedProductIds: ['rx-bupropion'],
    message: 'Bupropion lowers seizure threshold. Heavy alcohol use further lowers seizure threshold. Patients who drink heavily should not take bupropion. If alcohol cessation is planned, note that abrupt alcohol withdrawal ALSO lowers seizure threshold.',
    clinicalNote: 'Bupropion seizure risk is dose-dependent (~0.4% at 450mg/day). Alcohol lowers seizure threshold independently. Heavy alcohol use + bupropion = compounded seizure risk. FDA label contraindicates bupropion during abrupt discontinuation of alcohol. Active heavy drinkers (>3 drinks/day) should not be prescribed bupropion. If patient plans to reduce alcohol intake, coordinate timing — do not start bupropion during acute alcohol withdrawal period. [EVIDENCE: Strong. FDA label contraindication.]',
    isActive: true,
    updatedAt: '2026-03-14',
  },

];
